import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountBalance, AccountParentAccountInfo, CircleButton, withStyles } from 'vopay';
import { BodyText, Card, CopyButton, Title, TruncatedText } from 'UI';
import AccountBreadcrumbs from '@/components/Entities/Accounts/AccountBreadcrumbs/AccountBreadcrumbs';
import AccountStatus from '@/components/Entities/Accounts/AccountStatus/AccountStatus';
import ActiveIcon from '@/components/Accounts/Account/svg/Active.svg';
import PendingIcon from '@/components/Accounts/Account/svg/Pending.svg';
import type { AccountCredentials } from './accountCredentials';
import styles from './AccountCard.module.scss';

const cn = withStyles(styles);

const MASKED_SECRET = '••••••••••••';

type AccountCardProps = {
  account: AccountBalance;
  /** Null hides the credentials strip — the caller decides who may see it and when (Active only). */
  credentials: AccountCredentials | null;
  /**
   * Draws the account's ancestors as a trail above its name, each a route to that parent; for a
   * sub-account on the account page. Omitted (the drawer, a top-level account), no trail is drawn.
   */
  onParentAccountClick?: (parentAccount: AccountParentAccountInfo) => void;
};

type CredentialTileProps = {
  label: string;
  value: string;
  testId: string;
  /** Masked by default with a show / hide toggle; for the secret. */
  sensitive?: boolean;
};

function CredentialTile({ label, value, testId, sensitive = false }: CredentialTileProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountHeaderCards.AccountCard' });
  const [isRevealed, setIsRevealed] = useState(false);

  const isMasked = sensitive && !isRevealed;

  return (
    <div className={cn('credentialTile')}>
      <BodyText size="sm" weight="md">
        {label}
      </BodyText>

      <div className={cn('credentialValue')}>
        <BodyText className={cn('credentialText')} data-testid={testId}>
          {/* No explicit `information`: the tooltip falls back to what is shown, so a masked secret
              stays masked on hover rather than being revealed by it. */}
          <TruncatedText>{isMasked ? MASKED_SECRET : value}</TruncatedText>
        </BodyText>

        {sensitive && (
          <CircleButton
            icon={isRevealed ? 'visibility_off' : 'visibility'}
            colorScheme="action"
            aria-label={
              isRevealed
                ? t('hideSecret', { defaultValue: 'Hide shared secret' })
                : t('showSecret', { defaultValue: 'Show shared secret' })
            }
            aria-pressed={isRevealed}
            onClick={() => setIsRevealed((revealed) => !revealed)}
          />
        )}

        <CopyButton content={value} />
      </div>
    </div>
  );
}

/**
 * The account's identity card (spec 5.5): name, currency and status, with the API credentials strip
 * along the bottom when the caller passes credentials. Shared by the details drawer and the full
 * account page, which render the same card in their headers.
 */
export default function AccountCard({ account, credentials, onParentAccountClick }: AccountCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountHeaderCards.AccountCard' });

  return (
    <Card variant="section" className={cn('card')} data-testid="accountCard">
      <div className={cn('header')}>
        <div className={cn('identity')}>
          <img
            src={account.IsActive ? ActiveIcon : PendingIcon}
            alt={t(account.IsActive ? 'iconAltActive' : 'iconAltPending', {
              defaultValue: account.IsActive ? 'Active account' : 'Pending account',
            })}
            className={cn('icon')}
          />

          <div className={cn('name')}>
            {onParentAccountClick && account.ParentAccounts.length > 0 && (
              <AccountBreadcrumbs parentAccounts={account.ParentAccounts} onCrumbClick={onParentAccountClick} />
            )}

            {/* Figma: H3/Large Title 24/Emphasized over Body/16/Regular */}
            <Title level="3" className={cn('accountName')}>
              {account.AccountName}
            </Title>

            <BodyText className={cn('currency')}>{account.Currency}</BodyText>
          </div>
        </div>

        <AccountStatus account={account} activeDateCopy="activatedOn" layout="card" />
      </div>

      {credentials && (
        <div className={cn('credentials')} data-testid="accountCredentials">
          <CredentialTile
            label={t('accountId', { defaultValue: 'Account ID' })}
            value={credentials.accountId}
            testId="credentialAccountId"
          />

          <CredentialTile
            label={t('apiKey', { defaultValue: 'API Key' })}
            value={credentials.apiKey}
            testId="credentialApiKey"
          />

          <CredentialTile
            label={
              credentials.secretKind === 'sharedSecret'
                ? t('apiSharedSecret', { defaultValue: 'API Shared Secret' })
                : t('apiSignature', { defaultValue: 'API Signature' })
            }
            value={credentials.secret}
            testId="credentialSecret"
            sensitive
          />
        </div>
      )}
    </Card>
  );
}
